import React, { useState, useEffect } from "react";
import {
  Typography,
  Table,
  Space,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Checkbox,
  message,
  Breadcrumb,
  Tag,
  Tooltip,
  Badge,
  Row,
  Col,
  Card,
  Divider,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  HomeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { supabase } from "../lib/supabase";
import dayjs, { Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Add the relative time plugin to dayjs
dayjs.extend(relativeTime);

interface Task {
  id: number;
  created_at: string;
  booking_id: number | null;
  company_id: number | null;
  date: string | null;
  name: string | null;
  description: string | null;
  completed: boolean | null;
  team_member: number | null;
  completed_on: string | null;

  // Joined relations
  booking?: {
    id: number;
    reference: string;
    transport_type?: string;
    transport_types?: {
      abbreviation: string;
    };
  };
  company?: {
    id: number;
    name: string;
  };
  team_members?: {
    id: number;
    name: string;
  };
}

interface TeamMember {
  id: number;
  name: string;
  email: string;
}

interface Company {
  id: number;
  name: string;
}

interface Booking {
  id: number;
  reference: string;
  transport_type: string;
  company_name?: string;
  transport_types?: {
    abbreviation: string;
  };
}

interface FilterState {
  teamMember: number | undefined;
  completed: boolean | undefined;
  date: [Dayjs, Dayjs] | undefined;
  search: string;
  bookingId: number | undefined;
  companyId: number | undefined;
}

// Helper function to get abbreviation of transport type
const getTransportTypeAbbr = (
  booking?: { transport_types?: { abbreviation: string } } | null
): string => {
  if (!booking || !booking.transport_types) return "";

  try {
    return booking.transport_types.abbreviation || "";
  } catch (_err) {
    console.error("Error processing transport type abbreviation:", booking);
    return "";
  }
};

// Helper function to format booking ID with abbreviation
const formatBookingId = (
  booking?: { id: number; transport_types?: { abbreviation: string } } | null
): string => {
  if (!booking) return "";
  const abbr = getTransportTypeAbbr(booking);
  return `EFF${booking.id}${abbr}`;
};

const TasksAdmin = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("Add Task");
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    teamMember: undefined,
    completed: undefined,
    date: undefined,
    search: "",
    bookingId: undefined,
    companyId: undefined,
  });

  // Task statistics
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    overdue: 0,
    todayDue: 0,
  });

  const [teamMemberModalVisible, setTeamMemberModalVisible] = useState(false);
  const [teamMemberForm] = Form.useForm();
  const [editingTeamMember, setEditingTeamMember] = useState<TeamMember | null>(
    null
  );

  const fetchTasks = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from("tasks")
        .select(
          `
          *,
          booking:booking_id (
            id, 
            reference, 
            transport_type,
            transport_types:transport_type (abbreviation)
          ),
          company:company_id (id, name),
          team_members:team_member (id, name)
        `
        )
        .order("created_at", { ascending: false });

      // Apply filters
      if (filters.teamMember) {
        query = query.eq("team_member", filters.teamMember);
      }

      if (filters.completed !== undefined) {
        query = query.eq("completed", filters.completed);
      }

      if (filters.bookingId) {
        query = query.eq("booking_id", filters.bookingId);
      }

      if (filters.companyId) {
        query = query.eq("company_id", filters.companyId);
      }

      if (filters.date && filters.date.length === 2) {
        query = query
          .gte("date", filters.date[0].startOf("day").toISOString())
          .lte("date", filters.date[1].endOf("day").toISOString());
      }

      if (filters.search) {
        query = query.or(
          `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
        );
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      if (data) {
        setTasks(data);

        // Calculate statistics
        const now = dayjs();
        const today = now.startOf("day");
        const endOfDay = now.endOf("day");

        const total = data.length;
        const completed = data.filter((task) => task.completed).length;
        const pending = total - completed;
        const overdue = data.filter(
          (task) =>
            !task.completed && task.date && dayjs(task.date).isBefore(now)
        ).length;
        const todayDue = data.filter(
          (task) =>
            !task.completed &&
            task.date &&
            dayjs(task.date).isAfter(today) &&
            dayjs(task.date).isBefore(endOfDay)
        ).length;

        setStats({
          total,
          completed,
          pending,
          overdue,
          todayDue,
        });
      } else {
        setTasks([]);
        setStats({
          total: 0,
          completed: 0,
          pending: 0,
          overdue: 0,
          todayDue: 0,
        });
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      message.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .order("name");

      if (error) {
        throw error;
      }

      if (data) {
        setTeamMembers(data);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      message.error("Failed to load team members");
    }
  };

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from("company")
        .select("id, name")
        .order("name");

      if (error) {
        throw error;
      }

      if (data) {
        setCompanies(data);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      message.error("Failed to load companies");
    }
  };

  const fetchBookings = async (companyId?: number) => {
    try {
      let query = supabase
        .from("bookings")
        .select(
          `
          id, 
          reference,
          transport_type,
          transport_types:transport_type (abbreviation),
          company:company_id (id, name)
        `
        )
        .order("created_at", { ascending: false })
        .limit(100); // Limit to recent bookings

      // Filter by company if provided
      if (companyId) {
        query = query.eq("company_id", companyId);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      if (data) {
        // Transform the data to include company_name and transport_types
        const formattedBookings = data.map((booking: any) => ({
          id: booking.id,
          reference: booking.reference,
          transport_type: booking.transport_type,
          transport_types: booking.transport_types,
          company_name: booking.company?.name || null,
        }));
        setBookings(formattedBookings);
      } else {
        setBookings([]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      message.error("Failed to load bookings");
    }
  };

  useEffect(() => {
    trackFeatureUsage("admin_tasks");

    fetchTasks();
    fetchTeamMembers();
    fetchCompanies();
    fetchBookings();
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [filters]);

  useEffect(() => {
    if (!teamMemberModalVisible && teamMembers.length > 0) {
      fetchTeamMembers();
    }
  }, [teamMemberModalVisible]);

  const handleFormSubmit = async (values: Record<string, unknown>) => {
    try {
      const taskData = {
        booking_id: values.booking_id || null,
        company_id: values.company_id || null,
        date: values.date ? (values.date as Dayjs).toISOString() : null,
        name: values.name as string,
        description: values.description as string | null,
        team_member: values.team_member as number,
        completed: (values.completed as boolean) || false,
        completed_on: values.completed ? dayjs().toISOString() : null,
      };

      let result;

      if (editingTask) {
        // Update existing task
        result = await supabase
          .from("tasks")
          .update(taskData)
          .eq("id", editingTask.id);

        if (result.error) throw result.error;
        message.success("Task updated successfully");
      } else {
        // Insert new task
        result = await supabase.from("tasks").insert(taskData);

        if (result.error) throw result.error;
        message.success("Task created successfully");
      }

      // Reset and refetch
      setModalVisible(false);
      form.resetFields();
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      console.error("Error saving task:", error);
      message.error("Failed to save task");
    }
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setModalTitle("Edit Task");

    // If task has a company_id, fetch the related bookings
    if (task.company_id) {
      fetchBookings(task.company_id);
    }

    form.setFieldsValue({
      name: task.name,
      description: task.description,
      company_id: task.company_id,
      booking_id: task.booking_id,
      team_member: task.team_member,
      date: task.date ? dayjs(task.date) : null,
      completed: task.completed,
    });

    setModalVisible(true);
  };

  const handleDeleteTask = async (taskId: number) => {
    try {
      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this task?",
        onOk: async () => {
          const { error } = await supabase
            .from("tasks")
            .delete()
            .eq("id", taskId);

          if (error) throw error;

          message.success("Task deleted successfully");
          fetchTasks();
        },
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      message.error("Failed to delete task");
    }
  };

  const handleMarkComplete = async (task: Task) => {
    try {
      const { error } = await supabase
        .from("tasks")
        .update({
          completed: true,
          completed_on: dayjs().toISOString(),
        })
        .eq("id", task.id);

      if (error) throw error;

      message.success("Task marked as complete");
      fetchTasks();
    } catch (error) {
      console.error("Error updating task:", error);
      message.error("Failed to update task");
    }
  };

  const handleTeamMemberFormSubmit = async (
    values: Record<string, unknown>
  ) => {
    try {
      const teamMemberData = {
        name: values.name as string,
        email: values.email as string,
      };

      let result;

      if (editingTeamMember) {
        // Update existing team member
        result = await supabase
          .from("team_members")
          .update(teamMemberData)
          .eq("id", editingTeamMember.id);

        if (result.error) throw result.error;
        message.success("Team member updated successfully");
      } else {
        // Insert new team member
        result = await supabase.from("team_members").insert(teamMemberData);

        if (result.error) throw result.error;
        message.success("Team member added successfully");
      }

      // Reset and refetch
      setTeamMemberModalVisible(false);
      teamMemberForm.resetFields();
      setEditingTeamMember(null);
      fetchTeamMembers();
    } catch (error) {
      console.error("Error saving team member:", error);
      message.error("Failed to save team member");
    }
  };

  const handleEditTeamMember = (teamMember: TeamMember) => {
    setEditingTeamMember(teamMember);

    teamMemberForm.setFieldsValue({
      name: teamMember.name,
      email: teamMember.email,
    });

    setTeamMemberModalVisible(true);
  };

  const handleDeleteTeamMember = async (teamMemberId: number) => {
    try {
      // Check if any tasks are assigned to this team member
      const { data: tasksWithTeamMember, error: checkError } = await supabase
        .from("tasks")
        .select("id")
        .eq("team_member", teamMemberId);

      if (checkError) throw checkError;

      if (tasksWithTeamMember && tasksWithTeamMember.length > 0) {
        message.error(
          `Cannot delete team member. ${tasksWithTeamMember.length} tasks are assigned to this person.`
        );
        return;
      }

      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this team member?",
        onOk: async () => {
          const { error } = await supabase
            .from("team_members")
            .delete()
            .eq("id", teamMemberId);

          if (error) throw error;

          message.success("Team member deleted successfully");
          fetchTeamMembers();
        },
      });
    } catch (error) {
      console.error("Error deleting team member:", error);
      message.error("Failed to delete team member");
    }
  };

  const resetFilters = () => {
    setFilters({
      teamMember: undefined,
      completed: undefined,
      date: undefined,
      search: "",
      bookingId: undefined,
      companyId: undefined,
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Task) => (
        <div>
          <Text strong>{text}</Text>
          {record.company && (
            <div>
              <Tag color="purple">Company: {record.company.name}</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Booking",
      dataIndex: "booking_id",
      key: "booking_id",
      render: (_: unknown, record: Task) => {
        if (!record.booking) return "-";
        return <Tag color="blue">{formatBookingId(record.booking)}</Tag>;
      },
    },
    {
      title: "Assigned To",
      dataIndex: "team_member",
      key: "team_member",
      render: (teamMemberId: number, record: Task) => {
        const member = record.team_members;
        return member ? member.name : "Unassigned";
      },
    },
    {
      title: "Due Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => {
        if (!date) return "No deadline";

        const dueDate = dayjs(date);
        const isOverdue = !dayjs(date).isAfter(dayjs()) && dueDate.isValid();

        return (
          <div>
            <div>{dueDate.format("MMM D, YYYY")}</div>
            {isOverdue && <Tag color="red">Overdue</Tag>}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "completed",
      key: "completed",
      render: (completed: boolean) => {
        return completed ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>
            Completed
          </Tag>
        ) : (
          <Tag color="orange" icon={<ClockCircleOutlined />}>
            Pending
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: React.Key, record: Task) => (
        <Space>
          {!record.completed && (
            <Tooltip title="Mark Complete">
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleMarkComplete(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditTask(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteTask(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />

      <Title level={2}>Tasks Management</Title>

      {/* Task Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic title="Total Tasks" value={stats.total} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="Completed"
              value={stats.completed}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="Pending"
              value={stats.pending}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="Overdue"
              value={stats.overdue}
              valueStyle={{ color: "#ff4d4f" }}
              prefix={stats.overdue > 0 ? <Badge status="error" /> : null}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic
              title="Due Today"
              value={stats.todayDue}
              valueStyle={{ color: "#fa8c16" }}
              prefix={stats.todayDue > 0 ? <Badge status="warning" /> : null}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} md={4}>
            <Input
              placeholder="Search tasks"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              style={{ marginBottom: 8 }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Input
              placeholder="Booking ID"
              type="number"
              value={filters.bookingId}
              onChange={(e) => {
                const value = e.target.value
                  ? Number(e.target.value)
                  : undefined;
                setFilters({ ...filters, bookingId: value });
              }}
              style={{ marginBottom: 8 }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Company"
              style={{ width: "100%", marginBottom: 8 }}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                const childText = option?.children?.toString() || "";
                return (
                  childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                );
              }}
              value={filters.companyId}
              onChange={(value) => setFilters({ ...filters, companyId: value })}
            >
              {companies.map((company) => (
                <Option key={company.id} value={company.id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Assigned To"
              style={{ width: "100%", marginBottom: 8 }}
              allowClear
              value={filters.teamMember}
              onChange={(value) =>
                setFilters({ ...filters, teamMember: value })
              }
            >
              {teamMembers.map((member) => (
                <Option key={member.id} value={member.id}>
                  {member.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <Select
              placeholder="Status"
              style={{ width: "100%", marginBottom: 8 }}
              allowClear
              value={filters.completed}
              onChange={(value) => setFilters({ ...filters, completed: value })}
            >
              <Option value={false}>Pending</Option>
              <Option value={true}>Completed</Option>
            </Select>
          </Col>
          <Col xs={24} md={4}>
            <RangePicker
              style={{ width: "100%", marginBottom: 8 }}
              onChange={(dates) =>
                setFilters({ ...filters, date: dates as [Dayjs, Dayjs] })
              }
              value={filters.date}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button onClick={resetFilters} style={{ marginRight: 8 }}>
              Reset Filters
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setModalTitle("Add Task");
                setEditingTask(null);
                form.resetFields();
                fetchBookings();
                setModalVisible(true);
              }}
              style={{ marginRight: 8 }}
            >
              Add Task
            </Button>
            <Button
              onClick={() => {
                setEditingTeamMember(null);
                teamMemberForm.resetFields();
                setTeamMemberModalVisible(true);
              }}
              icon={<UserAddOutlined />}
            >
              Manage Team Members
            </Button>
          </Col>
        </Row>
      </div>

      {/* Tasks Table */}
      <Table
        dataSource={tasks}
        columns={columns}
        rowKey="id"
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <p style={{ margin: 0 }}>
              {record.description || "No description provided"}
            </p>
          ),
          rowExpandable: (record) => Boolean(record.description),
        }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
      />

      {/* Add/Edit Task Modal */}
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingTask(null);
          form.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setModalVisible(false);
              setEditingTask(null);
              form.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            Save
          </Button>,
        ]}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{ completed: false }}
        >
          <Form.Item
            name="name"
            label="Task Name"
            rules={[{ required: true, message: "Please enter a task name" }]}
          >
            <Input placeholder="Enter task name" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Enter task description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="company_id" label="Related Company">
                <Select
                  placeholder="Select a company"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const childText = option?.children?.toString() || "";
                    return (
                      childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                  onChange={(value) => {
                    // When company changes, reset booking field and refresh bookings
                    form.setFieldValue("booking_id", undefined);
                    if (value) {
                      fetchBookings(value as number);
                    } else {
                      fetchBookings();
                    }
                  }}
                >
                  {companies.map((company) => (
                    <Option key={company.id} value={company.id}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="booking_id" label="Related Booking">
                <Select
                  placeholder="Select a booking"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const childText = option?.children?.toString() || "";
                    return (
                      childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                >
                  {bookings.map((booking) => (
                    <Option key={booking.id} value={booking.id}>
                      {formatBookingId(booking)} -{" "}
                      {booking.reference || "No reference"}
                      {booking.company_name ? ` (${booking.company_name})` : ""}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="team_member"
                label="Assigned To"
                rules={[{ required: true, message: "Please assign the task" }]}
              >
                <Select
                  placeholder="Select a team member"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    const childText = option?.children?.toString() || "";
                    return (
                      childText.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    );
                  }}
                >
                  {teamMembers.map((member) => (
                    <Option key={member.id} value={member.id}>
                      {member.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="date" label="Due Date">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="completed" valuePropName="checked">
            <Checkbox>Mark as completed</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Team Members Modal */}
      <Modal
        title={editingTeamMember ? "Edit Team Member" : "Add Team Member"}
        open={teamMemberModalVisible}
        onCancel={() => {
          setTeamMemberModalVisible(false);
          setEditingTeamMember(null);
          teamMemberForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Form
            form={teamMemberForm}
            layout="vertical"
            onFinish={handleTeamMemberFormSubmit}
          >
            <Form.Item
              name="name"
              label="Name"
              rules={[
                { required: true, message: "Please enter team member name" },
              ]}
            >
              <Input placeholder="Enter name" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              {editingTeamMember ? "Update" : "Add"}
            </Button>
            <Button
              onClick={() => {
                setTeamMemberModalVisible(false);
                setEditingTeamMember(null);
                teamMemberForm.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form>
        </div>

        <Divider />

        <div>
          <Title level={5}>Team Members</Title>
          <Table
            dataSource={teamMembers}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: "Name",
                dataIndex: "name",
                key: "name",
              },
              {
                title: "Email",
                dataIndex: "email",
                key: "email",
              },
              {
                title: "Actions",
                key: "actions",
                width: 120,
                render: (_: unknown, record: TeamMember) => (
                  <Space>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEditTeamMember(record)}
                    />
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteTeamMember(record.id)}
                    />
                  </Space>
                ),
              },
            ]}
          />
        </div>
      </Modal>
    </div>
  );
};

export default TasksAdmin;

// Helper component for statistics display
const Statistic = ({
  title,
  value,
  valueStyle,
  prefix,
}: {
  title: string;
  value: number;
  valueStyle?: React.CSSProperties;
  prefix?: React.ReactNode;
}) => (
  <div>
    <Text type="secondary">{title}</Text>
    <div style={{ display: "flex", alignItems: "center", marginTop: 4 }}>
      {prefix}
      <Text style={{ fontSize: 24, ...valueStyle }}>{value}</Text>
    </div>
  </div>
);
